import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { type Notification, NotificationSettings } from '../types';
import NotificationService from '../services/notificationService';
import { useAuth } from './AuthContext';
import { useTimezone } from './TimezoneContext';
import { fetchNotifications, markNotificationAsRead, markAllNotificationsAsRead, registerFCMToken } from '../services/api';
import { requestNotificationPermission, onForegroundMessage } from '../services/firebase';
import { logger } from '../utils/logger';

const FCM_REGISTER_DELAY_MS = 1500;
const FCM_REGISTER_RETRY_MS = 5000;

interface NotificationContextType {
    notifications: Notification[];
    unreadCount: number;
    settings: NotificationSettings | null;
    loading: boolean;
    error: string | null;

    // Actions
    markAsRead: (notificationId: string) => void;
    markAllAsRead: () => void;
    updateSettings: (enabled: boolean) => Promise<void>;
    refreshNotifications: () => void;

    // Notification creation helpers
    createSopAddedNotification: (sopTitle: string, sopId: string, createdAt?: string) => void;
    createSopAccessChangedNotification: (sopTitle: string, sopId: string, createdAt?: string) => void;
    createUserOnboardedNotification: (managerName: string, onboardedUserName: string, onboardedUserEmail: string, onboardedUserRole: string, createdAt?: string) => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const useNotifications = () => {
    const context = useContext(NotificationContext);
    if (context === undefined) {
        throw new Error('useNotifications must be used within a NotificationProvider');
    }
    return context;
};

interface NotificationProviderProps {
    children: ReactNode;
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({ children }) => {
    const { user } = useAuth();
    const { userTimezone } = useTimezone();
    const [notifications, setNotifications] = useState<Notification[]>([]);
    const [settings, setSettings] = useState<NotificationSettings | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const notificationService = NotificationService.getInstance();

    // Load notifications from API
    const loadNotifications = useCallback(async () => {
        try {
            const response = await fetchNotifications();
            if (response.success && Array.isArray(response.data)) {
                setNotifications(response.data as Notification[]);
            }
        } catch (error) {
            // Handle load notifications error
        }
    }, []);

    // Register FCM token for push notifications; retries so token is stored even if first attempt fails (e.g. auth not ready)
    const registerFCMTokenForUser = useCallback(async () => {
        try {
            const token = await requestNotificationPermission();
            if (token) {
                await registerFCMToken(token);
            }
        } catch (error) {
            logger.debug('[Notifications] FCM token registration failed:', error);
        }
    }, []);

    // Load notifications when user changes, with delayed FCM registration and retry
    useEffect(() => {
        if (!user) return;

        loadNotifications();

        const delayId = window.setTimeout(() => {
            registerFCMTokenForUser();
        }, FCM_REGISTER_DELAY_MS);

        const retryId = window.setTimeout(() => {
            registerFCMTokenForUser();
        }, FCM_REGISTER_DELAY_MS + FCM_REGISTER_RETRY_MS);

        return () => {
            window.clearTimeout(delayId);
            window.clearTimeout(retryId);
        };
    }, [user, loadNotifications, registerFCMTokenForUser]);

    // When user returns to tab: re-register FCM token and sync notifications once (FCM in background is handled by service worker; app never receives onMessage, so we refetch once on visibility to update the bell)
    useEffect(() => {
        if (!user) return;

        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                registerFCMTokenForUser();
                loadNotifications();
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
    }, [user, loadNotifications, registerFCMTokenForUser]);

    const loadNotificationSettings = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);

            // For now, we'll create default settings with timezone
            // In a real app, you'd fetch this from your backend
            const defaultSettings: NotificationSettings = {
                enabled: true,
                userId: user?.id || '',
                timezone: userTimezone, // Include user's timezone
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString()
            };

            setSettings(defaultSettings);
            notificationService.updateSettings(defaultSettings);
        } catch (err: any) {
            setError(err?.message || 'Failed to load notification settings');
        } finally {
            setLoading(false);
        }
    }, [user, userTimezone, notificationService]);

    // Handle foreground FCM messages (when app is open and in focus)
    useEffect(() => {
        if (user) {
            // Listen for foreground push notifications
            const unsubscribe = onForegroundMessage((payload) => {

                // Show browser notification
                if (Notification.permission === 'granted') {
                    const notificationTitle = payload.notification?.title || 'Renegade Notification';
                    const notificationOptions = {
                        body: payload.notification?.body || '',
                        icon: '/faviconV2.png',
                        badge: '/faviconV2.png',
                        tag: payload.data?.notificationId || 'default',
                    };

                    new Notification(notificationTitle, notificationOptions);
                }

                // Refresh notifications list to show the new notification in the UI
                loadNotifications();
            });

            return () => {
                // Cleanup listener when user logs out or component unmounts
                if (typeof unsubscribe === 'function') {
                    unsubscribe();
                }
            };
        }
    }, [user, loadNotifications]);

    // Load notification settings when user changes
    useEffect(() => {
        if (user) {
            loadNotificationSettings();
        }
    }, [user, loadNotificationSettings]);

    const markAsRead = async (notificationId: string) => {
        try {
            await markNotificationAsRead(notificationId);
            // Reload notifications to get updated state
            loadNotifications();
        } catch (error) {
            // Handle mark as read error
        }
    };

    const markAllAsRead = async () => {
        try {
            // Marking all notifications as read
            await markAllNotificationsAsRead();
            // Successfully marked all notifications as read
            // Reload notifications to get updated state
            loadNotifications();
        } catch (error) {
            // Handle mark all as read error
        }
    };

    const updateSettings = async (enabled: boolean) => {
        try {
            setError(null);

            const updatedSettings: NotificationSettings = {
                enabled,
                userId: user?.id || '',
                createdAt: settings?.createdAt || new Date().toISOString(),
                updatedAt: new Date().toISOString()
            };

            setSettings(updatedSettings);
            notificationService.updateSettings(updatedSettings);

            // In a real app, you'd save this to your backend
            // await saveNotificationSettings(updatedSettings);
        } catch (err: any) {
            setError(err?.message || 'Failed to update notification settings');
            throw err;
        }
    };

    const refreshNotifications = () => {
        loadNotifications();
    };

    const createSopAddedNotification = (sopTitle: string, sopId: string, createdAt?: string) => {
        if (user && settings?.enabled) {
            notificationService.createSopAddedNotification(sopTitle, sopId, user.id, createdAt);
        }
    };

    const createSopAccessChangedNotification = (sopTitle: string, sopId: string, createdAt?: string) => {
        if (user && settings?.enabled) {
            notificationService.createSopAccessChangedNotification(sopTitle, sopId, user.id, createdAt);
        }
    };

    const createUserOnboardedNotification = (managerName: string, onboardedUserName: string, onboardedUserEmail: string, onboardedUserRole: string, createdAt?: string) => {
        if (user && settings?.enabled) {
            notificationService.createUserOnboardedNotification(managerName, onboardedUserName, onboardedUserEmail, onboardedUserRole, user.id, createdAt);
        }
    };

    const unreadCount = notifications.filter(n => !n.read).length;

    const value: NotificationContextType = {
        notifications,
        unreadCount,
        settings,
        loading,
        error,
        markAsRead,
        markAllAsRead,
        updateSettings,
        refreshNotifications,
        createSopAddedNotification,
        createSopAccessChangedNotification,
        createUserOnboardedNotification,
    };

    return (
        <NotificationContext.Provider value={value}>
            {children}
        </NotificationContext.Provider>
    );
};
